import axios from "axios";
import * as csv from "csvtojson";
import eol from "eol";
import _ from "lodash";
import React, { ChangeEvent } from "react";
import { connect } from "react-redux";
import { Dispatch } from "redux";

import Button from "@material-ui/core/Button";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import JSZip, { JSZipObject } from "jszip";
import { ITable } from "./table";

import {
  addFile,
  addIssues,
  openFile,
  resetStore,
  saveDataStore,
  setStatus,
  setZipName
} from "./actions";
import analyzeFile from "./analyze";
import { IFile, IIssue, IStatus } from "./types";

// import { resolve } from "path";

interface IProps {
  addFile: (file: Pick<IFile, "content" | "name" | "path">) => void;
  addIssue: (issue: IIssue) => void;
  openFile: (id: number) => void;
  resetStore: () => void;
  saveDataStore: (datastore: ITable) => void;
  setZipName: (zipName: string) => void;
  setStatus: (status: IStatus) => void;
}

interface IState {
  podIP: string;
}

const hashCode = (input: string) : number => {
  let hash: number = 0;

  for (let char of input ) {
    const chr: number = char.charCodeAt(0);
    hash  = ((hash << 5) - hash) + chr;
    hash |= 0; // Convert to 32bit integer
  }
  return hash;
};

class Open extends React.Component<IProps, IState> {
  constructor(props: IProps) {
    super(props);

    this.state = {
      podIP: ""
    };
  }

  public updatePodIP = (event: any) => {
    this.setState({
      podIP: event.target.value
    });
  };

  public saveFileToStore = (
    zip: JSZip,
    zipEntry: JSZipObject
  ): Promise<IFile | undefined> => {
    const path: string = zipEntry.name;
    const name: string = _.last(path.split("/")) || "";
    const open: boolean = false;

    console.log(`Found file: path=[${path}] name=[${name}]`);

    if (name === "datastoreConfig.csv") {
      return zip
        .file(path)
        .async("text")
        .then(content => {
          return csv({
            noheader: true,
            output: "csv"
          })
            .fromString(content)
            .then((rows: string[][]) => {
              this.props.saveDataStore({
                columns: ["key", "value"],
                values: rows
              });

              return undefined;
            });
        });
    }

    if (
      name.includes("logcat") ||
      name.includes("tombstone") ||
      name === "timestamps.txt" ||
      name === "events.html"
    ) {
      return new Promise((resolve, reject) =>
        zip
          .file(path)
          .async("text")
          .then((content: string) => {
            const file: IFile = {
              content,
              id: Math.random() * 1e9,
              name,
              open,
              path
            };
            resolve(file);
          })
          .catch((e: Error) => console.log(e.message))
      );
    }

    return Promise.resolve(undefined); // catch all
  };

  public sortFiles = (files: IFile[]): IFile[] => {
    // the most recent file is named 'logcat.txt', all others are 'logcat.txt.n' where n is an element of set [1..10]
    // normalize the names so we can sort them
    const normalizeName = (name: string): string => {
      const mapping = {
        "logcat.txt": "logcat.txt.00",
        "logcat.txt.1": "logcat.txt.01",
        "logcat.txt.2": "logcat.txt.02",
        "logcat.txt.3": "logcat.txt.03",
        "logcat.txt.4": "logcat.txt.04",
        "logcat.txt.5": "logcat.txt.05",
        "logcat.txt.6": "logcat.txt.06",
        "logcat.txt.7": "logcat.txt.07",
        "logcat.txt.8": "logcat.txt.08",
        "logcat.txt.9": "logcat.txt.09",
        // tslint:disable-next-line
        "logcat.txt.10": "logcat.txt.10"
      };
      return typeof mapping[name] === "undefined" ? name : mapping[name];
    };
    const sorted: IFile[] = files.sort((a: IFile, b: IFile) => {
      const normalizedA = normalizeName(a.name);
      const normalizedB = normalizeName(b.name);
      return normalizedA > normalizedB ? -1 : 1;
    });
    return sorted;
  };

  public handleTombstones = (files: IFile[]): void => {
    const tombstones: IFile[] = files.filter((file: IFile) => {
      return file.name.includes("tombstone");
    });
    console.log(`Found ${tombstones.length} tombstones`);
    // const tombstones = files.filter(file => file.name.includes('tombstone'))
    if (!tombstones || tombstones.length === 0) {
      return;
    }

    const timestamps = files.find(file => file.name === "timestamps.txt");
    if (!timestamps) {
      return;
    }

    const timestampLines = eol
      .split(timestamps.content)
      .filter(line => line.includes("tombstone"));
    if (timestampLines.length === 0) {
      console.error(`Failed to get timestamps for tombstomnes`);
      return;
    }

    /* timestamps.txt file format
    may have more lines here
    drwxrwx--x system   system            2014-12-31 17:00 modem
    -rw------- system   system    2263083 2018-09-29 23:35 tombstone_00
    .........
    -rw------- system   system    2347573 2018-09-29 00:10 tombstone_09
    */
   
    /* new style:
    -rw------- 1 system system  30146 2020-07-22 16:58 tombstone_00
    */

    timestampLines.forEach(line => {
      // the tombstone filename is the final thing in the line,
      // so find it's starting index and cut off to the end.
      const name: string = line.substr(line.indexOf(`tombstone`));

      // we'll need a regex because the line position changes depending
      // on file size.
      const timeMatches: RegExpMatchArray | null = line.match(/\d{4}-\d{2}-\d{2} \d{2}:\d{2}/);
      if (!timeMatches) {
        console.error(`No times in line: [${line}]`);
        return;
      }
      const timeString: string = timeMatches[0];
      const tombstone: IFile | undefined = tombstones.find(
        file => file.name === name
      );

      if (!tombstone) {
        console.error(`Couldn't find tombstone for timestamp: [${name}]`)
        return;
      }

      const fileId: number = hashCode(`${tombstone.path}${timeString}`);
      console.log(`Recognized tombstone: name=[${name}] fileId=[${fileId}]`);

      this.props.addFile({
        content: tombstone.content,
        id: fileId,
        name,
        open: false,
        path: tombstone.path
      } as IFile);
      // this is pretty gross but saves a bunch of boilerplate detecting issues
      // and lets us just open the file to the first line the same every one menu item handles the click
      // and is what we do to jump to individual logcat files in the big concatenated file
      this.props.addIssue({
        fileId: fileId,
        id: fileId,
        info:
          "A tombstone file generated by an Android crash. Tombstones are sorted in order by time, not by name.",
        lineNumber: 0,
        name,
        secondary: timeString,
        type: "TOMBSTONE_FILE"
      });
    });
  };

  public handleEvents = (files: IFile[]): void => {
    const events = files.filter(file =>
      file.name.includes("events")
    );
    
    const event: IFile | undefined = events.find(
      file => file.name === "events.html"
    );
    if (!event) {
      console.error(`Couldn't find event file`)
      return;
    }

    const eventsFile: IFile = {
      content: event.content,
      id: Math.random() * 1e9,
      name: "events",
      open: false,
      path: event.path
    }
    this.props.addFile(eventsFile);
  };


  public handleFileInput = (event: ChangeEvent<HTMLInputElement>) => {
    const firstFile: File = event.target.files![0];
    if (!firstFile) {
      return; // cancel was hit on the dialog, nothing to process
    }

    this.processZipFile(firstFile);
  };

  public processZipFile = (inputFile: ArrayBuffer | File) => {
    this.props.resetStore();
    this.props.setZipName("logs.zip");
    this.props.setStatus("EXTRACTING");

    const success = (zip: JSZip) => {
      console.log(`File unzipped`);
      const promises: Array<Promise<IFile | undefined>> = [];

      zip.forEach((_relPath: string, zipEntry: JSZipObject) => {
        promises.push(this.saveFileToStore(zip, zipEntry));
      });
      Promise.all(promises).then((files: IFile[]) => {
        const actualFiles = files.filter(file => !!file);
        this.handleTombstones(actualFiles);
        this.handleEvents(actualFiles)
        const logcats = actualFiles.filter(file =>
          file.name.includes("logcat")
        );

        const sorted = this.sortFiles(logcats);
        // logcat files can sometimes be duplicated in two different spots, let's dedup them
        const uniq = _.uniqBy(sorted, file => file.name);
        // join all the logcat files together in order
        const concatenatedLogcat: IFile = {
          content: uniq.reduce(
            (acc, cur) => (acc += `###${cur.name}###\n${cur.content}`),
            ""
          ),
          id: Math.random() * 1e9,
          name: "Combined Logcat",
          open: true,
          path: "thisisnotarealpath"
        };
        this.props.addFile(concatenatedLogcat);
        this.props.setStatus("ANALYZING");
        setTimeout(() => {
          // analyze our single concatenated file
          analyzeFile(concatenatedLogcat);
          this.props.setStatus("BROWSING");
        }, 0);
      });
    };
    const error = (e: Error) => {
      console.log(e.message);
      window.alert(
        `Corrupt zip: ${e.message}\n\nTry unzipping the file and rezipping the output, or fetching logs from the pod again.`
      );
    };
    const unzip = (file: ArrayBuffer | File) => {
      console.log(`Going to unzip the file`);
      JSZip.loadAsync(file).then(success, error);
    };

    unzip(inputFile);
  };

  public retrieveFile = () => {
    const podIP = this.state.podIP;
    this.props.setStatus("DOWNLOADING");
    axios({
      method: "get",
      responseType: "arraybuffer",
      timeout: 0,
      url: `https://${podIP}/diag/logs.zip`
    })
      .then(response => response.data)
      .then(zipFile => this.processZipFile(zipFile))
      .catch(err => {
        console.error(
          `Unable to get logs for the given pod IP address: ${err}`
        );
        this.props.setStatus("BROWSING");
      });
  };

  public render() {
    const style = {
      input: {
        border: "1px solid #ccc",
        borderRadius: "4px",
        display: "block",
        fontSize: "1rem",
        padding: "0.5rem 0.25rem"
      }
    };

    return (
      <React.Fragment>
        <List>
          <ListItem divider>
            <input
              accept=".zip"
              style={{ display: "none" }}
              id="contained-button-file"
              type="file"
              onChange={this.handleFileInput}
            />
            <label htmlFor="contained-button-file">
              <Button variant="contained" component="span" color={"primary"}>
                open logs
              </Button>
            </label>
          </ListItem>
          <ListItem>
            <input
              style={style.input}
              type="text"
              onChange={this.updatePodIP}
              value=""
              placeholder="Pod IP Address"
            />
          </ListItem>
          <ListItem>
            <Button
              variant="contained"
              component="div"
              color={"primary"}
              disabled={true}
              onClick={this.retrieveFile}
            >
              Download (WIP)
            </Button>
          </ListItem>
        </List>
      </React.Fragment>
    );
  }
}

const mapDispatchToProps = (dispatch: Dispatch) => ({
  addFile: (file: IFile) => dispatch(addFile(file)),
  addIssue: (issue: IIssue) => dispatch(addIssues([issue])),
  openFile: (id: number) => dispatch(openFile(id)),
  resetStore: () => dispatch(resetStore()),
  saveDataStore: (datastore: ITable) => dispatch(saveDataStore(datastore)),
  setStatus: (status: IStatus) => dispatch(setStatus(status)),
  setZipName: (zipName: string) => dispatch(setZipName(zipName))
});

export default connect(
  undefined,
  mapDispatchToProps
)(Open);



// WEBPACK FOOTER //
// ./src/Open.tsx